import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext({ theme: "dark", toggle: () => {} });

const STORAGE_KEY = "am-theme";

// NOTE on localStorage:
// We persist ONLY the user's visual theme preference ("dark" | "light") here.
// This is a non-sensitive UI preference (not auth, not PII, not a token), so
// localStorage is the correct primitive — survives page reloads, syncs across
// tabs via the `storage` event, and is read by a tiny inline bootstrap script
// in public/index.html to prevent a flash of the wrong theme on first paint.
// Do not move other data into this key.

const isDev =
  typeof process !== "undefined" &&
  process.env &&
  process.env.NODE_ENV !== "production";

const warnInDev = (msg, err) => {
  if (!isDev) return;
  // eslint-disable-next-line no-console
  console.warn(msg, err);
};

const readInitialTheme = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch (err) {
    // Storage can be unavailable in privacy mode / sandboxed iframes.
    warnInDev("[ThemeContext] localStorage read failed:", err);
  }
  return "dark";
};

const persistTheme = (next) => {
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch (err) {
    warnInDev("[ThemeContext] localStorage write failed:", err);
  }
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(readInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    persistTheme(theme);
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  // Memoised — prevents needless re-renders of consumers.
  const value = useMemo(() => ({ theme, toggle }), [theme, toggle]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
